import { useState, useEffect, useMemo } from "react";
import { FlagIcon } from "./FlagIcon";
import { MapPin, Clock, Phone, Mail, Globe, ArrowRight, ShieldCheck, Navigation, MessageCircle, ExternalLink, Sparkles } from "lucide-react";
import { useCmsSections } from "@/hooks/use-cms";

export interface HubLocation {
  id: string;
  country: string;
  countryName: string;
  city: string;
  region: string;
  address: string;
  timeZone: string;
  phone: string;
  whatsapp: string;
  email: string;
  x: number; // Percentage on user world map
  y: number; // Percentage on user world map
  badgeAlign: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "bottom-center";
  desk: string;
  role: string;
}

export const DEFAULT_GLOBAL_HUBS: HubLocation[] = [
  {
    id: "ca",
    country: "CA",
    countryName: "Canada",
    city: "Toronto",
    region: "Ontario",
    address: "1315 Finch Avenue West, Toronto ON M3J 2G6",
    timeZone: "America/Toronto",
    phone: "+1 416 555 0188",
    whatsapp: "14165550188",
    email: "canada@eliadasportsagency.com",
    x: 25.8,
    y: 33.8,
    badgeAlign: "top-left",
    desk: "Canadian Premier League & Youth Pathway Center",
    role: "Canadian Pathway Hub",
  },
  {
    id: "us",
    country: "US",
    countryName: "United States",
    city: "Detroit",
    region: "Michigan",
    address: "20270 Plymouth Rd, Detroit, MI 48228",
    timeZone: "America/Detroit",
    phone: "+1 313 555 0194",
    whatsapp: "13135550194",
    email: "usa@eliadasportsagency.com",
    x: 24.2,
    y: 37.2,
    badgeAlign: "bottom-left",
    desk: "North American MLS, USL & Collegiate Placement Hub",
    role: "Americas Operations",
  },
  {
    id: "gb",
    country: "GB",
    countryName: "United Kingdom",
    city: "Kent",
    region: "Greater London",
    address: "1A Town Square, Erith, Kent DA8 1RE",
    timeZone: "Europe/London",
    phone: "+44 20 8311 0022",
    whatsapp: "442083110022",
    email: "uk@eliadasportsagency.com",
    x: 46.2,
    y: 26.8,
    badgeAlign: "top-left",
    desk: "UK & Premier League / EFL Representation Desk",
    role: "UK Operations & Legal",
  },
  {
    id: "de",
    country: "DE",
    countryName: "Germany",
    city: "Grainet",
    region: "Bayern",
    address: "Unterseilberg 9, 94143, Grainet, Bayern",
    timeZone: "Europe/Berlin",
    phone: "+49 8585 9699 0",
    whatsapp: "4915770904476",
    email: "germany@eliadasportsagency.com",
    x: 50.0,
    y: 28.6,
    badgeAlign: "top-right",
    desk: "Central European Scouting & Bundesliga Transfer Desk",
    role: "European Headquarters",
  },
  {
    id: "ng",
    country: "NG",
    countryName: "Nigeria",
    city: "Lagos",
    region: "Lagos State",
    address: "95 Ogudu Road, Ogudu, Lagos",
    timeZone: "Africa/Lagos",
    phone: "+234 803 555 0177",
    whatsapp: "2348035550177",
    email: "nigeria@eliadasportsagency.com",
    x: 48.6,
    y: 53.8,
    badgeAlign: "bottom-right",
    desk: "West African Scouting & Elite Academy Scouting Hub",
    role: "African Talent Development",
  },
];

function parseMetadata(meta: string | null | undefined): Record<string, any> {
  if (!meta) return {};
  try {
    return JSON.parse(meta);
  } catch {
    return {};
  }
}

function useLiveTime(timeZone: string) {
  const [time, setTime] = useState("");

  useEffect(() => {
    const updateTime = () => {
      try {
        const formatter = new Intl.DateTimeFormat("en-GB", {
          timeZone,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
        setTime(formatter.format(new Date()));
      } catch {
        setTime("--:--:--");
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [timeZone]);

  return time;
}

export function InteractiveWorldMap() {
  const { data: sections = [] } = useCmsSections();

  // Find CMS section for global presence
  const cmsSection = useMemo(() => {
    return sections.find(
      (s: any) => s.key === "about_global_presence" || s.key === "home_global_presence"
    );
  }, [sections]);

  const meta = useMemo(() => parseMetadata(cmsSection?.metadata), [cmsSection]);

  const badgeText = meta.badgeText || "Global Transfer Desks & Headquarters";
  const heading = meta.heading || "5 Strategic Hubs.";
  const headingHighlight = meta.headingHighlight || "Direct Global Access.";
  const subtext =
    cmsSection?.content ||
    meta.subtext ||
    "Pulsating nodes across Europe, North America, and Africa with dedicated flight corridors and direct transfer desks. Select any node to view live local time and direct contact channels.";
  const watermarkText = meta.watermarkText || "Eliada Global Hub Network";

  // Parse hubs from CMS metadata or fallback to default
  const hubs: HubLocation[] = useMemo(() => {
    if (meta.hubs && Array.isArray(meta.hubs) && meta.hubs.length > 0) {
      return meta.hubs.map((h: any, idx: number) => ({
        id: h.id || `hub-${idx}`,
        country: (h.country || "DE").toUpperCase(),
        countryName: h.countryName || h.country || "Agency Hub",
        city: h.city || "Hub City",
        region: h.region || "",
        address: h.address || h.addr || "",
        timeZone: h.timeZone || "Europe/Berlin",
        phone: h.phone || "+49 8585 9699 0",
        whatsapp: (h.whatsapp || "4915770904476").replace(/\D/g, ""),
        email: h.email || "contact@eliadasportsagency.com",
        x: typeof h.x === "number" ? h.x : DEFAULT_GLOBAL_HUBS[idx % DEFAULT_GLOBAL_HUBS.length].x,
        y: typeof h.y === "number" ? h.y : DEFAULT_GLOBAL_HUBS[idx % DEFAULT_GLOBAL_HUBS.length].y,
        badgeAlign: h.badgeAlign || DEFAULT_GLOBAL_HUBS[idx % DEFAULT_GLOBAL_HUBS.length]?.badgeAlign || "top-right",
        desk: h.desk || "International Transfer & Scouting Desk",
        role: h.role || "Agency Hub",
      }));
    }
    return DEFAULT_GLOBAL_HUBS;
  }, [meta.hubs]);

  const [selectedHubId, setSelectedHubId] = useState<string>(hubs[3]?.id || hubs[0]?.id || "de");

  const selectedHub = useMemo(() => {
    return hubs.find((h) => h.id === selectedHubId) || hubs[0] || DEFAULT_GLOBAL_HUBS[0];
  }, [hubs, selectedHubId]);

  const activeTime = useLiveTime(selectedHub.timeZone);

  return (
    <div className="w-full bg-[#18191c] border border-yellow-500/25 rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl relative overflow-hidden backdrop-blur-xl">
      {/* Subtle atmospheric light cones */}
      <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 relative z-10">
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-bold uppercase tracking-widest mb-3">
            <Globe className="w-4 h-4 text-yellow-400 animate-spin-slow" />
            {badgeText}
          </div>
          <h3 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            {heading} <span className="text-yellow-400">{headingHighlight}</span>
          </h3>
          <p className="text-slate-400 text-sm mt-1.5 max-w-xl leading-relaxed">
            {subtext}
          </p>
        </div>

        {/* Live Active Clock Badge */}
        <div className="bg-[#22242a]/95 border border-slate-700/80 rounded-2xl px-5 py-3.5 flex items-center gap-4 shrink-0 shadow-2xl backdrop-blur-md">
          <div className="relative flex items-center justify-center">
            <div className="w-3.5 h-3.5 rounded-full bg-emerald-400" />
            <div className="absolute w-3.5 h-3.5 rounded-full bg-emerald-400 animate-ping opacity-75" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-widest text-slate-400 flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-yellow-400" />
              <span>{selectedHub.city} Live Time</span>
            </div>
            <div className="text-xl font-mono font-black text-yellow-400 mt-0.5">
              {activeTime || "00:00:00"}{" "}
              <span className="text-xs text-slate-400 font-sans font-normal">
                ({selectedHub.timeZone.split("/")[1]?.replace("_", " ")})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Interactive Map Container */}
      <div className="relative w-full aspect-[2/1] min-h-[380px] sm:min-h-[500px] bg-[#22242a] rounded-2xl border border-slate-700/80 shadow-2xl mb-8 overflow-hidden flex items-center justify-center select-none group">
        
        {/* World Map SVG with Image Embed & Glowing Vector Connections */}
        <svg
          viewBox="0 0 1000 500"
          className="w-full h-full object-contain relative z-0"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {/* Gradient for flight lines */}
            <linearGradient id="flightGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#eab308" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#eab308" stopOpacity="0.9" />
            </linearGradient>
            
            {/* Glow Filter */}
            <filter id="vectorGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Embedded Full High-Res World Map */}
          <image
            href={`${import.meta.env.BASE_URL}images/world-map.png`}
            x="0"
            y="0"
            width="1000"
            height="500"
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-full"
          />

          {/* Dynamic Flight Corridor between Western Americas & Europe */}
          <path
            d="M 250 175 Q 350 80 462 134"
            stroke="url(#flightGradient)"
            strokeWidth="2.2"
            strokeDasharray="5 5"
            fill="none"
            filter="url(#vectorGlow)"
          />

          {/* UK <-> Europe Central HQ */}
          <path
            d="M 462 134 Q 480 120 500 143"
            stroke="#eab308"
            strokeWidth="2.5"
            fill="none"
            filter="url(#vectorGlow)"
          />

          {/* Europe HQ <-> West Africa */}
          <path
            d="M 500 143 Q 535 205 486 269"
            stroke="url(#flightGradient)"
            strokeWidth="2.2"
            strokeDasharray="5 5"
            fill="none"
            filter="url(#vectorGlow)"
          />

          {/* Americas <-> West Africa direct bridge */}
          <path
            d="M 242 186 Q 350 255 486 269"
            stroke="rgba(234, 179, 8, 0.4)"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            fill="none"
          />
        </svg>

        {/* Pulsating Radar Nodes & Smart Positioned Callout Badges */}
        {hubs.map((hub) => {
          const isSelected = selectedHub.id === hub.id;

          const getCalloutOffset = (align: HubLocation["badgeAlign"]) => {
            switch (align) {
              case "top-left":
                return "bottom-full right-1/2 -mb-1 mr-2 flex-col items-end";
              case "top-right":
                return "bottom-full left-1/2 -mb-1 ml-2 flex-col items-start";
              case "bottom-left":
                return "top-full right-1/2 -mt-1 mr-2 flex-col items-end";
              case "bottom-right":
                return "top-full left-1/2 -mt-1 ml-2 flex-col items-start";
              default:
                return "top-full left-1/2 -translate-x-1/2 mt-2 flex-col items-center";
            }
          };

          return (
            <div
              key={hub.id}
              style={{ left: `${hub.x}%`, top: `${hub.y}%` }}
              className="absolute -translate-x-1/2 -translate-y-1/2 z-20"
            >
              {/* Radar Beacon at Exact Geographic Coordinate */}
              <button
                type="button"
                onClick={() => setSelectedHubId(hub.id)}
                className="relative flex items-center justify-center w-6 h-6 focus:outline-none group/pin cursor-pointer"
                title={`${hub.city}, ${hub.countryName} - ${hub.role}`}
              >
                {/* Concentric Pulsating Radar Waves */}
                <span
                  className={`absolute w-8 h-8 rounded-full transition-all ${
                    isSelected
                      ? "bg-yellow-400/40 animate-ping"
                      : "bg-yellow-500/20 group-hover/pin:bg-yellow-400/30 animate-pulse"
                  }`}
                />
                <span
                  className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${
                    isSelected
                      ? "bg-yellow-400 border-white ring-4 ring-yellow-400/50 scale-125 shadow-lg shadow-yellow-400/50"
                      : "bg-yellow-500 border-slate-900 group-hover/pin:scale-125"
                  }`}
                />
              </button>

              {/* Offset Callout Badge (No Overlap) */}
              <div
                className={`absolute ${getCalloutOffset(
                  hub.badgeAlign
                )} flex pointer-events-auto transition-transform duration-200`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedHubId(hub.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full shadow-2xl border transition-all text-xs font-black whitespace-nowrap cursor-pointer ${
                    isSelected
                      ? "bg-yellow-500 text-black border-yellow-300 ring-2 ring-yellow-400/40 shadow-yellow-500/30 scale-105"
                      : "bg-[#141518]/90 text-white border-slate-700 hover:border-yellow-400 hover:bg-[#202227] hover:scale-105 backdrop-blur-md"
                  }`}
                >
                  <FlagIcon country={hub.country} size="sm" />
                  <span className="text-[11px] font-bold tracking-tight">
                    {hub.city}
                  </span>
                  <span
                    className={`text-[9px] uppercase font-mono px-1 py-0.2 rounded font-extrabold ${
                      isSelected
                        ? "bg-black/20 text-black"
                        : "bg-slate-800 text-yellow-400"
                    }`}
                  >
                    {hub.country}
                  </span>
                </button>
              </div>
            </div>
          );
        })}

        {/* Watermark in bottom-right */}
        <div className="absolute bottom-3 right-4 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 bg-slate-900/90 px-3.5 py-1.5 rounded-full border border-slate-700 shadow-md">
          <Navigation className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
          <span>{watermarkText}</span>
        </div>
      </div>

      {/* Hub Selection Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
        {hubs.map((hub) => {
          const isSelected = selectedHub.id === hub.id;
          return (
            <button
              key={hub.id}
              onClick={() => setSelectedHubId(hub.id)}
              className={`text-left p-4 rounded-2xl border transition-all duration-200 flex flex-col justify-between cursor-pointer ${
                isSelected
                  ? "bg-yellow-500/10 border-yellow-500 shadow-lg shadow-yellow-500/15 -translate-y-1"
                  : "bg-[#22242a]/80 border-slate-700/80 hover:border-slate-600 hover:bg-[#282a30]"
              }`}
            >
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <FlagIcon country={hub.country} size="md" />
                  <span
                    className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      isSelected
                        ? "bg-yellow-400 text-black font-black"
                        : "bg-slate-800 text-slate-400"
                    }`}
                  >
                    {hub.country}
                  </span>
                </div>
                <h4 className="text-white font-extrabold text-base">{hub.city}</h4>
                <p className="text-slate-400 text-xs mt-0.5">{hub.countryName}</p>
                <p className="text-slate-400 text-[11px] mt-2.5 line-clamp-2 leading-relaxed">
                  {hub.address}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-700/70 flex items-center justify-between text-xs">
                <span className="text-yellow-400 font-semibold text-[11px]">{hub.role}</span>
                <ArrowRight
                  className={`w-3.5 h-3.5 transition-transform ${
                    isSelected ? "text-yellow-400 translate-x-1" : "text-slate-500"
                  }`}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Hub Deep-Dive Action Card */}
      <div className="mt-6 p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-[#22242a] via-[#22242a] to-[#1c1d22] border border-yellow-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl shrink-0 mt-1 sm:mt-0">
            <FlagIcon country={selectedHub.country} size="lg" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-widest text-yellow-400">
                {selectedHub.role}
              </span>
              <span className="text-slate-600">•</span>
              <span className="text-xs text-emerald-400 font-bold flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Verified Agency Office
              </span>
            </div>
            <h4 className="text-xl sm:text-2xl font-black text-white mt-1">
              {selectedHub.city}, {selectedHub.countryName}
            </h4>
            <p className="text-slate-300 text-xs sm:text-sm mt-1 font-medium">{selectedHub.desk}</p>
            <p className="text-slate-400 text-xs mt-1.5 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
              {selectedHub.address}
            </p>
          </div>
        </div>

        {/* Direct Action Contacts: WhatsApp, Direct Call, and Email */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
          <a
            href={`https://wa.me/${selectedHub.whatsapp}?text=${encodeURIComponent(`Hello Eliada Sports Agency (${selectedHub.city} Hub), I would like to inquire about representation and scouting opportunities.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white text-xs font-black transition-colors shadow-sm"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            WhatsApp
          </a>
          <a
            href={`mailto:${selectedHub.email}`}
            className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold border border-slate-700 transition-colors shadow-sm"
          >
            <Mail className="w-3.5 h-3.5 text-yellow-400" />
            Email
          </a>
          <a
            href={`tel:${selectedHub.phone.replace(/\s/g, "")}`}
            className="flex-1 md:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-extrabold transition-all shadow-md active:scale-95"
          >
            <Phone className="w-3.5 h-3.5" />
            Direct Call
          </a>
        </div>
      </div>
    </div>
  );
}
